import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Table } from 'melp-design/components';
import { ModalProps, useModalContext } from 'store/modal';
import {
  useDeleteEmployeeEmployeeGroupAssignmentHistoryEntry,
  useEmployeeEmployeeGroupAssignmentHistory,
} from 'store/assignments';
import moment from 'moment';
import { Stack } from '@mui/material';
import { useConfirm } from 'store/confirm';
import { UpdateAssignmentModal } from 'containers/assignments';

interface Props extends ModalProps {
  employeeId: string;
  employeeGroupId: string;
  readonly: boolean;
}

export const EmployeeEmployeeGroupAssignmentHistory: FC<Props> = ({
  employeeId,
  employeeGroupId,
  closeModal,
  readonly,
}) => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();

  const { data: history, isLoading } =
    useEmployeeEmployeeGroupAssignmentHistory(employeeId, employeeGroupId);

  const { mutate: deleteHistoryEntry, isLoading: isDeleting } =
    useDeleteEmployeeEmployeeGroupAssignmentHistoryEntry();

  return (
    <Modal.Content
      title={t('permissions.historyModule')}
      actions={[
        {
          variant: 'neutral-outline',
          onClick: () => closeModal({ action: 'CLOSE' }),
          label: t('common.close'),
        },
      ]}
    >
      <Table
        data={history ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'from',
            header: t('alternatives.dateRangeFrom'),
            cell: ({ row: { original: entry } }) => {
              return moment(entry.from).format('l LT');
            },
          },
          {
            id: 'to',
            header: t('common.to'),
            cell: ({ row: { original: entry } }) => {
              return entry.to ? moment(entry.to).format('l LT') : '-';
            },
          },
          {
            id: 'actions',
            cell: ({ row: { original: entry } }) =>
              readonly ? null : (
                <Stack flexDirection="row" gap={1} justifyContent="flex-end">
                  <Button
                    label={t('common.edit')}
                    variant="neutral-outline"
                    size="sm"
                    onClick={() => {
                      openModal({
                        component: UpdateAssignmentModal,
                        props: {
                          assignmentId: entry.id,
                          relation: 'employee/employeeGroup',
                          initialValues: {
                            from: entry.from,
                            to: entry.to,
                          },
                          withTime: true,
                        },
                      });
                    }}
                  />
                  <Button
                    label={t('actions.delete')}
                    variant="danger-outline"
                    size="sm"
                    onClick={async () => {
                      const { confirmed } = await confirmation(
                        t('common.areYouSureToDelete', {
                          title: t('alternatives.dateRange'),
                        }),
                      );

                      if (confirmed) {
                        deleteHistoryEntry({ assignmentId: entry.id });
                      }
                    }}
                    disabled={isDeleting}
                  />
                </Stack>
              ),
          },
        ]}
      />
    </Modal.Content>
  );
};
